import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ImageComponent from '../../../common/Image';
import NoImageComponent from '../../../common/NoImage';
import { useDispatch, useSelector } from 'react-redux';
import { addOnDetails, addOnSubmit, setSelectedAddOns, setSubStage, stageSubmit } from '../../../../slices/eligibility/eligibilitySlice';
import { toast } from 'react-toastify';
import Loader from '../../../common/Loader';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DatePicker from './datePicker';
// import { tracer } from "../../../../otel"; // adjust the path to your otel setup

function Addon() {
    const href = "#";
    const urlParams = new URLSearchParams(window.location.search);
    const referenceId = urlParams.get('referenceId');
    const dispatch = useDispatch();
    const [loader, setLoader] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [cholaMsModal, setCholaMsModal] = useState(false);
    const [hdfcModal, setHdfcModal] = useState(false);
    const [showHdfcConfirm, setShowHdfcConfirm] = useState(false);
    const [hdfcFormValues, setHdfcFormValues] = useState(null);
    const [premium, setPremium] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [cholaMsFormValues, setCholaMsFormValues] = useState(null);

    // array to hold all selected add-ons
    const { selectedAddOns } = useSelector(state => state.eligibility)

    // track which cards are flipped
    const [flippedCards, setFlippedCards] = useState(new Set());

    const { userAddOnDetails, hdfcAddOnDetails, addOnData } = useSelector(state => state.eligibility);

    useEffect(() => {
        if (referenceId) {
            setLoader(true);
            dispatch(addOnDetails({ reference_id: referenceId }))
                .unwrap()
                .then(response => {
                    setLoader(false);
                    if (response?.response_code !== 'S100') {
                        !response?.payload?.error &&
                            toast.error(response?.response_string);
                    }
                })
                .catch(() => {
                    setLoader(false);
                    toast.error('An error occurred while fetching product data');
                });
        }
    }, []); // empty dependency to run once on mount


    // Function to submit add-ons after Chola MS modal is done or skipped
    const submitAddOns = async (values) => {
        const resData = {
            reference_id: referenceId,
            cross_sell_list: selectedAddOns,
            chola_ms_data: cholaMsFormValues ? cholaMsFormValues : userAddOnDetails,
            hdfc_data: hdfcFormValues ? hdfcFormValues : hdfcAddOnDetails
        };

        setLoader(true);
        const response = await dispatch(addOnSubmit(resData)).unwrap();
        setLoader(false);

        if (response?.response_code === 'S100') {
            dispatch(setSubStage("do_summary"));
        } else {
            !response?.payload?.error &&
                toast.error(response?.response_string);
        }
    };

    // Submit handler - show Chola MS modal if selected, else submit immediately
    const submitDetail = () => {
        submitAddOns();
    };

    const hasAnyMedicalYes = (values) => {
        for (let i = 1; i <= 12; i++) {
            if (values[`medicalReport${i}`] === 'yes') return true;
        }
        return false;
    };

    // Formik initial values and validation schema for Chola MS modal form
    const cholaMsInitialValues = {
        firstName: userAddOnDetails?.firstName ?? '',
        lastName: userAddOnDetails?.lastName ?? '',
        dob: userAddOnDetails?.dob ?? '',
        relation: userAddOnDetails?.relation ?? '',
        gender: userAddOnDetails?.gender ?? '',
        agree: userAddOnDetails?.agree ?? false
    };

    const hdfcInitialValues = {
        hdfcFirstName: hdfcAddOnDetails?.hdfcFirstName ?? '',
        hdfcLastName: hdfcAddOnDetails?.hdfcLastName ?? '',
        hdfcDob: hdfcAddOnDetails?.hdfcDob ?? '',
        hdfcRelation: hdfcAddOnDetails?.hdfcRelation ?? '',
        hdfcGender: hdfcAddOnDetails?.hdfcGender ?? '',
        hdfcAgree: hdfcAddOnDetails?.hdfcAgree ?? false,
        medicalReport1: 'no',
        medicalReport2: 'no',
        medicalReport3: 'no',
        medicalReport4: 'no',
        medicalReport5: 'no',
        medicalReport6: 'no',
        medicalReport7: 'no',
        medicalReport8: 'no',
        medicalReport9: 'no',
        medicalReport10: 'no',
        medicalReport11: 'no',
        medicalReport12: 'no',
    };
    console.log(hdfcAddOnDetails, "hdfc")

    const cholaMsValidationSchema = Yup.object({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        dob: Yup.string().required('Date of Birth is required').max(
                new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                ),
                "You must be at least 18 years old"
            ),
        relation: Yup.string().required('Relationship is required'),
        agree: Yup.boolean().oneOf([true], 'You must accept the declaration'),
    });

    const hdfcValidationSchema = Yup.object({
        hdfcFirstName: Yup.string().required('First name is required'),
        hdfcLastName: Yup.string().required('Last name is required'), 
        hdfcDob: Yup.date()
            .required("Date of Birth is required")
            .max(
                new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                ),
                "You must be at least 18 years old"
            ),
        hdfcRelation: Yup.string().required('Relationship is required'),
        hdfcAgree: Yup.boolean().oneOf([true], 'You must accept the declaration'),
    });

    // Function to handle card flip
    const handleCardFlip = (addonIndex, e) => {
        e.stopPropagation(); // Prevent triggering the modal
        setFlippedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(addonIndex)) {
                newSet.delete(addonIndex);
            } else {
                newSet.add(addonIndex);
            }
            return newSet;
        });
    };

    // Function to get addon features based on type
    const getAddonFeatures = (addon) => {
        const code = addon?.cross_sell_code?.toLowerCase();
        const provider = addon?.cross_sell_code?.toLowerCase();

        if (code === 'adld') {
            return ['Accidental Death Coverage', 'Life Disability Protection', 'Instant Claim Processing', 'Family Security'];
        } else if (provider === 'acko') {
            return ['Zero Depreciation Cover', 'Engine Protection', 'Return to Invoice', '24/7 Roadside Assistance'];
        } else if (provider === 'chola ms') {
            return ['Health Coverage', 'Critical Illness', 'Hospitalization Benefits', 'Preventive Care'];
        } else if (code === 'amc') {
            return ['Extended Warranty', 'Free Maintenance', 'Genuine Parts', 'Expert Service'];
        }
        return ['Premium Protection', 'Comprehensive Coverage', 'Easy Claims', 'Dedicated Support'];
    };

    const openAddonModal = (addon) => {
        setModalData(addon);

        // Auto-select premium if only one option exists
        const premiums = [
            addon?.premium_1 ? { value: addon.premium_1, year: 1 } : null,
            addon?.premium_2 ? { value: addon.premium_2, year: 2 } : null,
            addon?.premium_3 ? { value: addon.premium_3, year: 3 } : null,
        ].filter(Boolean);

        if (premiums.length === 1) {
            setPremium(premiums[0].value);
            setSelectedYear(premiums[0].year);
        } else {
            // clear selection if multiple options
            setPremium(null);
            setSelectedYear(null);
        }
    };

    console.log(addOnData, selectedAddOns, "wwww")

    // Prevent background scroll when modal is open
    useEffect(() => {
        if (modalData || cholaMsModal || hdfcModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        // Cleanup on unmount
        return () => {
            document.body.style.overflow = '';
        };
    }, [modalData, cholaMsModal, hdfcModal]);

    const openModal = (e, cross_sell_code, addon) => {

        if (e.target.checked) {

            if (cross_sell_code?.toLowerCase() === 'ms') {
                setCholaMsModal(addon);
                return;
            }

            else if (cross_sell_code?.toLowerCase() === 'hdfc') {
                setHdfcModal(addon);
                return;
            }
            else {

                const limit = addon.cross_sell_limit ? addon.cross_sell_limit : null;   // 1 for Life, null for ADLD
                const type = addon.cross_sell_type;

                const selectedOfSameType = selectedAddOns.filter(
                    (item) => item.cross_sell_type === type
                );

                // -----------------------------
                // ❌ Prevent selecting more than limit
                // -----------------------------
                if (limit !== null && selectedOfSameType.length >= limit) {
                    return; // block selecting another Life addon
                }

                if (e.target.checked) {
                    // collect premium options
                    const premiums = [
                        addon?.premium_1 ? { value: addon.premium_1, year: 1 } : null,
                        addon?.premium_2 ? { value: addon.premium_2, year: 2 } : null,
                        addon?.premium_3 ? { value: addon.premium_3, year: 3 } : null,
                    ].filter(Boolean);

                    if (premiums.length === 1) {
                        // auto-select if only one premium exists
                        const selected = {
                            ...addon,
                            cross_sell_product: addon.cross_sell_code,
                            cross_sell_code: addon.cross_sell_code,
                            crossel_amount: premiums[0].value,
                            year: "" + premiums[0].year,
                        };

                        const existingIndex = selectedAddOns.findIndex(
                            (item) => item.cross_sell_product === selected.cross_sell_product
                        );

                        const newArray =
                            existingIndex !== -1
                                ? Object.assign([...selectedAddOns], { [existingIndex]: selected })
                                : [...selectedAddOns, selected];

                        dispatch(setSelectedAddOns(newArray));
                    } else {
                        // open modal if more than one option
                        openAddonModal(addon);
                    }
                } else {
                    // unchecked → remove
                    dispatch(
                        setSelectedAddOns(
                            selectedAddOns.filter(
                                (item) => item.cross_sell_code !== addon.cross_sell_code
                            )
                        )
                    );
                }
            }
        } else {
            // unchecked → remove
            dispatch(
                setSelectedAddOns(
                    selectedAddOns.filter(
                        (item) => item.cross_sell_code !== addon.cross_sell_code
                    )
                )
            );
        }
    }

    const pushModal = (addon) => {
        const premiums = [
            addon?.premium_1 ? { value: addon.premium_1, year: 1 } : null,
            addon?.premium_2 ? { value: addon.premium_2, year: 2 } : null,
            addon?.premium_3 ? { value: addon.premium_3, year: 3 } : null,
        ].filter(Boolean);

        if (premiums.length === 1) {
            // ✅ auto-select if only one premium exists
            const selected = {
                ...addon,
                cross_sell_product: addon.cross_sell_code,
                cross_sell_code: addon.cross_sell_code,
                crossel_amount: premiums[0].value,
                year: "" + premiums[0].year,
            };

            const existingIndex = selectedAddOns.findIndex(
                (item) => item.cross_sell_product === selected.cross_sell_product
            );

            const newArray =
                existingIndex !== -1
                    ? Object.assign([...selectedAddOns], { [existingIndex]: selected })
                    : [...selectedAddOns, selected];

            dispatch(setSelectedAddOns(newArray));
        } else {
            // 👇 open modal if more than one option
            openAddonModal(addon);
        }
    }

    return (
        <>
            {loader && <Loader />}
            <div className="col-md-9 col-sm-12 Side-bank-Right">
                <div className="row mt-3">
                    <div className="static-tab">
                        <div className="d-flex justify-content-between">
                            <p className="Pname-i text-decoration-none dark-grey small">
                                .. <span className="icon-arrowright-L1" text-decoration-none dark-blue small></span>{" "}
                                <span
                                    role="button"
                                    onClick={() => dispatch(setSubStage("scheme"))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            dispatch(setSubStage("scheme"));
                                        }
                                    }}
                                >
                                    Scheme Details
                                </span>
                                <span className="icon-arrowright-L1 text-decoration-none dark-blue small"></span>{" "}
                                <span
                                    role="button"
                                    onClick={() => dispatch(setSubStage("emi_plan"))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            dispatch(setSubStage("emi_plan"));
                                        }
                                    }}
                                >
                                    EMI Details
                                </span>
                                <span className="icon-arrowright-L1 blue"></span>{" "}
                                <a href={href} className="text-decoration-none blue">
                                    Optional Services
                                </a>
                            </p>
                        </div>
                    </div>

                    {Array.isArray(addOnData) && addOnData.map((addon, index) => (
                        <div className="col-12 position-relative" key={index}>
                            <div className="addon-card-container">
                                <div className={`addon-card ${flippedCards.has(index) ? 'flipped' : ''}`}>
                                    {/* Front Face */}
                                    {/* {addon?.cross_sell_type !== 'insurance' && <div className="addon-card-front">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                const span = tracer.startSpan("addon_card_click", {
                                                    attributes: {
                                                        addonCode: addon?.cross_sell_code,
                                                        addonName: addon?.cross_sell_name,
                                                        type: addon?.cross_sell_type,
                                                        premium: addon?.premium_1,
                                                        "custom.reference_id": referenceId, // <- add your custom reference_id here
                                                    },
                                                });
                                                span.end();
                                            }}
                                        >
                                            <div className={`multiselect ${addon.crosssell_name === "ANMOL PRIVILEGE" ? "anmol-res" : ""} ${addon.crosssell_name === "ANMOL PRIVILEGE" ? "top-12" : ""} ${addon.crosssell_name === "OA-HOME ASSURE" ? "top-21" : ""}`} >
                                                <input
                                                    className="form-check-input form-check-input-addon"
                                                    type="checkbox"
                                                    id={`flexCheckDefault${index}`}
                                                    checked={selectedAddOns.some(
                                                        item => item.cross_sell_code === addon.cross_sell_code
                                                    )}
                                                    disabled={
                                                        addon.cross_sell_limit !== null &&
                                                        selectedAddOns.filter(item => item.cross_sell_type === addon.cross_sell_type).length >= addon.cross_sell_limit &&
                                                        !selectedAddOns.some(item => item.cross_sell_code === addon.cross_sell_code)
                                                    }
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        openModal(e, addon.cross_sell_code, addon)
                                                    }}
                                                />

                                            </div>
                                            <img loading="lazy"
                                                src={addon?.img}
                                                alt={addon?.cross_sell_code}
                                                className={"img-fluid"}
                                                onClick={addon?.note_description && Object.keys(addon.note_description).length > 0 ? handleCardFlip.bind(null, index) : undefined}
                                                onKeyDown={(e) => {
                                                    if ((e.key === 'Enter' || e.key === ' ') && addon?.note_description && Object.keys(addon.note_description).length > 0) {
                                                        handleCardFlip(index, e);
                                                    }
                                                }}
                                                role={addon?.note_description && Object.keys(addon.note_description).length > 0 ? "button" : undefined}
                                                tabIndex={addon?.note_description && Object.keys(addon.note_description).length > 0 ? 0 : -1}
                                            />
                                            <div role='button' 
                                                onClick={addon?.note_description && Object.keys(addon.note_description).length > 0 ? handleCardFlip.bind(null, index) : undefined} className={`position-absolute ${addon?.note_description && Object.keys(addon.note_description).length > 0 ? 'addon-text' : 'addon-no-text'}`}>
                                                <img loading="lazy"
                                                    src={addon?.logo_img}
                                                    alt={addon?.name}
                                                    className='w-50'
                                                />
                                                <p className="text-white fs-6 my-0 mt-2 fw-semibold">{addon?.cross_sell_name}</p>
                                                <p className="text-white small my-0 mb-2 fw-bold"><span className="font-serif mx-1">₹</span>{Number(addon?.premium_1).toLocaleString('en-IN')} Onwards</p>
                                                {addon?.note_description && Object.keys(addon.note_description).length > 0 && (
                                                    <p
                                                        className="text-white small my-0 text-decoration-underline know-more-btn"
                                                        onClick={handleCardFlip.bind(null, index)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                handleCardFlip(index, e);
                                                            }
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        KNOW MORE <span className='icon-arrowright-L1'></span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>} */}

                                    <div className="addon-card-front">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            // onClick={() => {
                                            //     const span = tracer.startSpan("addon_card_click", {
                                            //         attributes: {
                                            //             addonCode: addon?.cross_sell_code,
                                            //             addonName: addon?.cross_sell_name,
                                            //             type: addon?.cross_sell_type,
                                            //             premium: addon?.premium_1,
                                            //             "custom.reference_id": referenceId,
                                            //         },
                                            //     });
                                            //     span.end();
                                            // }}
                                        >
                                            <div className={`multiselect ${addon.crosssell_name === "ANMOL PRIVILEGE" ? "anmol-res" : ""} ${addon.crosssell_name === "ANMOL PRIVILEGE" ? "top-12" : ""} ${addon.crosssell_name === "OA-HOME ASSURE" ? "top-21" : ""}`} >
                                                <input
                                                    className="form-check-input form-check-input-addon"
                                                    type="checkbox"
                                                    id={`flexCheckDefault${index}`}
                                                    checked={selectedAddOns.some(
                                                        item => item.cross_sell_code === addon.cross_sell_code
                                                    )}
                                                    // disabled={
                                                    //     (addon.cross_sell_limit !== null) &&
                                                    //     selectedAddOns.filter(item => item.cross_sell_type === addon.cross_sell_type).length >= addon.cross_sell_limit &&
                                                    //     !selectedAddOns.some(item => item.cross_sell_code === addon.cross_sell_code)
                                                    // }
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        openModal(e, addon.cross_sell_code, addon)
                                                    }}
                                                />

                                            </div>
                                            <img loading="lazy"
                                                src={addon?.img}
                                                alt={addon?.cross_sell_code}
                                                className={"img-fluid"}
                                                onClick={addon?.note_description && Object.keys(addon.note_description).length > 0 ? handleCardFlip.bind(null, index) : undefined}
                                                onKeyDown={(e) => {
                                                    if ((e.key === 'Enter' || e.key === ' ') && addon?.note_description && Object.keys(addon.note_description).length > 0) {
                                                        handleCardFlip(index, e);
                                                    }
                                                }}
                                                role={addon?.note_description && Object.keys(addon.note_description).length > 0 ? "button" : undefined}
                                                tabIndex={addon?.note_description && Object.keys(addon.note_description).length > 0 ? 0 : -1}
                                            />
                                            <div role='button'
                                                onClick={addon?.note_description && Object.keys(addon.note_description).length > 0 ? handleCardFlip.bind(null, index) : undefined} className={`position-absolute ${addon?.note_description && Object.keys(addon.note_description).length > 0 ? 'addon-text' : 'addon-no-text'}`}>
                                                <p className="text-white fs-6 my-0 mt-4 fw-semibold">{addon?.cross_sell_label}</p>
                                                <p className="text-white small my-0 mb-2"><span className="font-serif mx-1 fs-6">₹</span><span className='fs-6'>{Number(addon?.premium_1).toLocaleString('en-IN')} {addon?.cross_sell_code?.toLowerCase() === 'ew' && <span className='onwards-text'>Onwards</span>}</span></p>

                                            </div>
                                            <div className='addon-footer-bottom'>
                                                <div className='col-4 addon-footer-logo'>
                                                    <img loading="lazy"
                                                        src={addon?.logo_img}
                                                        alt={addon?.name}
                                                        className={addon?.crosssell_service_provider_description?.toLowerCase() === 'acko' && 'w-30px' || (addon?.crosssell_service_provider_description?.toLowerCase() === 'one assist' || addon?.crosssell_service_provider_description?.toLowerCase() === 'cholamandalam ms') && 'w-70px' || 'w-50'}
                                                    />
                                                </div>
                                                <div className='col-4 ms-3'>
                                                    <p className="text-white small my-0 fw-bold text-center">₹{addon?.emi_onwards?.toLocaleString('en-IN')} / Month</p>
                                                </div>
                                                <div className='col-4 ms-4'>
                                                    {addon?.note_description && Object.keys(addon.note_description).length > 0 && (
                                                        <p
                                                            className="text-white small my-0 text-decoration-underline know-more-btn"
                                                            onClick={handleCardFlip.bind(null, index)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    handleCardFlip(index, e);
                                                                }
                                                            }}
                                                            role="button"
                                                            tabIndex={0}
                                                        >
                                                            KNOW MORE <span className='icon-arrowright-L1'></span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Back Face */}
                                    <div className="addon-card-back" onClick={handleCardFlip.bind(null, index)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                handleCardFlip(index, e);
                                            }
                                        }}>
                                        <img loading="lazy"
                                            src={addon?.back_img}
                                            alt={addon?.cross_sell_code}
                                            className={"img-fluid"}
                                        />
                                        <div className="position-absolute addon-back-text">
                                            {addon?.note_description && typeof addon.note_description === 'string' &&
                                                addon.note_description.split(';').map((feature, featureIndex) => (
                                                    <div key={featureIndex} className="text-start mb-2 d-flex align-items-start">
                                                        <div>
                                                            <i className="icon-Essetional-Tick-1 fw-bold"></i>
                                                        </div>
                                                        <div className="ms-2 addon-back-text-div">
                                                            {feature.trim()}
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal for add-on detail selection */}
            {modalData && (
                <>
                    <div
                        className="modal fade show d-block p-0"
                        id="panComp"
                        tabIndex="-1"
                        aria-labelledby="panCompLabel"
                        aria-hidden="true"
                        onClick={() => {
                            setModalData(null);
                            setPremium(null);
                            setSelectedYear(null);
                        }}
                    >
                        <div
                            className="modal-dialog bottom-modal"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation()
                                }
                            }}
                        >
                            <div className="modal-content addon_popup-div">
                                <div className="modal-header p-2">
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setModalData(false); // proceed anyway
                                        }}
                                    ></button>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="d-flex align-items-center">
                                            <div className="add-on-img mb-4">
                                                <img loading="lazy"
                                                    src={modalData?.model_img}
                                                    alt={modalData?.cross_sell_code}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 ">
                                        <div className="d-flex gap-2 genderbg-btn">
                                            {modalData?.premium_1 &&
                                                <>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="options"
                                                        id="option1"
                                                        checked={premium === modalData?.premium_1}
                                                        autoComplete="off"
                                                        readOnly
                                                    />
                                                    <label
                                                        className="btn btn-outline-primary"
                                                        role="button"
                                                        onClick={() => {
                                                            setPremium(modalData?.premium_1);
                                                            setSelectedYear(1);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setPremium(modalData?.premium_1);
                                                                setSelectedYear(1);
                                                            }
                                                        }}
                                                        htmlFor="option1"
                                                        tabIndex={0}
                                                    >
                                                        1 Year
                                                    </label>
                                                </>}

                                            {modalData?.premium_2 &&
                                                <>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="options"
                                                        id="option2"
                                                        checked={premium === modalData?.premium_2}
                                                        autoComplete="off"
                                                        readOnly
                                                    />
                                                    <label
                                                        className="btn btn-outline-primary"
                                                        role="button"
                                                        onClick={() => {
                                                            setPremium(modalData?.premium_2);
                                                            setSelectedYear(2);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setPremium(modalData?.premium_2);
                                                                setSelectedYear(2);
                                                            }
                                                        }}
                                                        htmlFor="option2"
                                                        tabIndex={0}
                                                    >
                                                        2 Years
                                                    </label>
                                                </>}

                                            {modalData?.premium_3 &&
                                                <>
                                                    <input
                                                        type="radio"
                                                        className="btn-check"
                                                        name="options"
                                                        id="option3"
                                                        checked={premium === modalData?.premium_3}
                                                        autoComplete="off"
                                                        readOnly
                                                    />
                                                    <label
                                                        className="btn btn-outline-primary"
                                                        role="button"
                                                        onClick={() => {
                                                            setPremium(modalData?.premium_3);
                                                            setSelectedYear(3);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setPremium(modalData?.premium_3);
                                                                setSelectedYear(3);
                                                            }
                                                        }}
                                                        htmlFor="option3"
                                                        tabIndex={0}
                                                    >
                                                        3 Years
                                                    </label>
                                                </>}
                                        </div>
                                    </div>

                                    {premium && (
                                        <div className="col-12 d-flex justify-content-end mt-3">
                                            <div className="emi-calculate fw-semibold"><span className="font-serif mx-1">₹</span>{Number(premium).toLocaleString('en-IN')}/+ GST</div>
                                        </div>
                                    )}

                                    <div className="col-sm-12 mt-3">
                                        <div className="d-flex justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-secondary d-none"
                                                onClick={() => {
                                                    setModalData(false); // proceed anyway
                                                }}
                                            >
                                                Back
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={() => {
                                                    if (!premium || !selectedYear) {
                                                        toast.warn("Please select coverage period");
                                                        return;
                                                    }
                                                    const newAddon = {
                                                        ...modalData,
                                                        cross_sell_product: modalData.cross_sell_code,
                                                        cross_sell_code: modalData.cross_sell_code,
                                                        crossel_amount: premium,
                                                        year: "" + selectedYear,
                                                    };
                                                    const newArray = (() => {
                                                        const existingIndex = selectedAddOns.findIndex(
                                                            (item) => item.cross_sell_product === newAddon.cross_sell_product
                                                        );

                                                        if (existingIndex !== -1) {
                                                            const arr = [...selectedAddOns];
                                                            arr[existingIndex] = newAddon;
                                                            return arr;
                                                        } else {
                                                            return [...selectedAddOns, newAddon];
                                                        }
                                                    })();

                                                    dispatch(setSelectedAddOns(newArray));
                                                    setModalData(null);
                                                    setPremium(null);
                                                    setSelectedYear(null);
                                                }}
                                            >
                                                Proceed
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-backdrop fade show"></div>
                </>
            )}

            {/* Chola MS Modal with Formik */}
            {cholaMsModal && ReactDOM.createPortal(
                <>
                    <div
                        className="modal fade show d-block"
                        id="cholaMsModal"
                        tabIndex="-1"
                        aria-labelledby="cholaMsModalLabel"
                        aria-hidden="true"
                        onClick={() => setCholaMsModal(false)}
                    >
                        <div className="modal-dialog bottom-modal" onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation()
                            }
                        }} onClick={e => e.stopPropagation()}>
                            <div className="modal-content addon_popup-div pt-0">
                                {/* Modal Header */}
                                <div className="modal-header">
                                    <div className="top-draw"></div>
                                </div>

                                <div className='modal-height-custom overflow-x-hidden mb-5'>
                                    <div className="d-flex align-items-center justify-content-between mb-4 w-100">
                                        <h6 className="fw-semibold mb-3 mt-3">Nominee Info. (Chola MS Insurance)</h6>
                                    </div>

                                    <Formik
                                        initialValues={cholaMsInitialValues}
                                        validationSchema={cholaMsValidationSchema}
                                        onSubmit={(values, { setSubmitting }) => {
                                            console.log(values, "eeeee")
                                            setCholaMsFormValues(values);
                                            setSubmitting(true);
                                            setCholaMsModal(false);
                                            setSubmitting(false);
                                            pushModal(cholaMsModal)
                                        }}
                                    >
                                        {({ values, isSubmitting, setFieldValue }) => (
                                            <Form>
                                                <div className="row floating-top">
                                                    <div className="col-12">
                                                        <div className="input-container has-icon">
                                                            <Field name="firstName">
                                                                {({ field, form }) => (
                                                                    <input
                                                                        {...field}
                                                                        type="text"
                                                                        id="firstName"
                                                                        className="form-control input-underline p-0"
                                                                        placeholder=" "
                                                                        required
                                                                        onChange={(e) => {
                                                                            const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                                                            form.setFieldValue(field.name, value);
                                                                        }}
                                                                    />
                                                                )}
                                                            </Field>
                                                            <label className="label form-label" htmlFor='firstName'>First Name</label>
                                                            <div className="underline"></div>
                                                            <ErrorMessage name="firstName" component="div" className="text-danger small" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row floating-bottom">
                                                    <div className="col-12">
                                                        <div className="input-container has-icon">
                                                            <Field name="lastName">
                                                                {({ field, form }) => (
                                                                    <input
                                                                        {...field}
                                                                        type="text"
                                                                        id="lastName"
                                                                        className="form-control input-underline p-0"
                                                                        placeholder=" "
                                                                        required
                                                                        onChange={(e) => {
                                                                            const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                                                            form.setFieldValue(field.name, value);
                                                                        }}
                                                                    />
                                                                )}
                                                            </Field>
                                                            <label className="label form-label" htmlFor='firstName'>Last Name</label>
                                                            <div className="underline"></div>
                                                            <ErrorMessage name="lastName" component="div" className="text-danger small" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row floating-bottom">
                                                    <div className="col-12">
                                                        <label htmlFor="dob" className="form-label">Date of Birth</label>
                                                        <DatePicker
                                                            value={values?.dob}
                                                            onChange={(value) => setFieldValue('dob', value)}
                                                            label=""
                                                            required={true}
                                                            id="dob"
                                                            name="dob"
                                                            placeholder=""
                                                            autoComplete="off"
                                                        />
                                                        <ErrorMessage name="dob" component="div" className="text-danger small" />
                                                    </div>
                                                </div>

                                                <div className="row floating-bottom">
                                                    <div className="col-md-12">
                                                        <label htmlFor="relation" className="form-label">Relationship</label>
                                                        <Field as="select" name="relation" id="relation" className="form-select input-underline kyc-summary-dropdown p-0 nominee-select">
                                                            <option value="">Select</option>
                                                            <option value="mother">Mother</option>
                                                            <option value="father">Father</option>
                                                            <option value="spouse">Spouse</option>
                                                            <option value="daughter">Daughter</option>
                                                            <option value="son">Son</option>
                                                        </Field>
                                                        <ErrorMessage name="relation" component="div" className="text-danger small" />
                                                    </div>
                                                </div>

                                                <div className="row addon-footer-top">
                                                    <div className="col-12">
                                                        <label className="form-check-label fs-12">
                                                            <Field type="checkbox" name="agree" className="form-check-input" />
                                                            &nbsp;&nbsp;I hereby declare and confirm that the above details are true and correct.
                                                        </label>
                                                        <ErrorMessage name="agree" component="div" className="text-danger small" />
                                                    </div>
                                                </div>

                                                {/* Modal Footer */}
                                                <footer className="sticky-bottom-1 pe-24 cust-foot">

                                                    <div className="d-flex justify-content-end">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-primary"
                                                            disabled={isSubmitting}
                                                        >
                                                            Submit
                                                        </button>
                                                    </div>
                                                </footer>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>,
                document.body
            )}

            {/* HDFC Modal with Formik */}
            {hdfcModal && ReactDOM.createPortal(
                <>
                    <div
                        className="modal fade show d-block"
                        id="hdfcModal"
                        tabIndex="-1"
                        aria-labelledby="hdfcModalLabel"
                        aria-hidden="true"
                        onClick={() => {
                            setCholaMsModal(false);
                            setHdfcModal(false);
                            setShowHdfcConfirm(false);
                        }}
                    >
                        <div className="modal-dialog bottom-modal" onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation()
                            }
                        }} onClick={e => e.stopPropagation()}>
                            <div className="modal-content addon_popup-div pt-0">
                                {/* Modal Header */}
                                <div className="modal-header">
                                    <div className="top-draw"></div>
                                </div>

                                <div className='modal-height-custom overflow-x-hidden'>
                                    <div className="d-flex align-items-center justify-content-between mb-4 w-100">
                                        <h6 className="fw-semibold mb-3 mt-3">Nominee Info. (HDFC Insurance)</h6>
                                    </div>

                                    <Formik
                                        initialValues={hdfcInitialValues}
                                        validationSchema={hdfcValidationSchema}
                                        onSubmit={(values, { setSubmitting }) => {
                                            if (hasAnyMedicalYes(values)) {
                                                setHdfcFormValues(values);
                                                setShowHdfcConfirm(hdfcModal);
                                                setSubmitting(false);
                                            } else {
                                                setHdfcFormValues(values);
                                                setShowHdfcConfirm(false);
                                                setCholaMsModal(false);
                                                setHdfcModal(false);
                                                setSubmitting(false);
                                                pushModal(hdfcModal)
                                            }
                                        }}
                                    >
                                        {({ values, isSubmitting, setFieldValue }) => (
                                            <Form>
                                                {/* ...duplicate form fields from Chola MS modal... */}
                                                <div className="row floating-top">
                                                    <div className="col-12">
                                                        <div className="input-container has-icon">
                                                            <Field name="hdfcFirstName">
                                                                {({ field, form }) => (
                                                                    <input
                                                                        {...field}
                                                                        type="text"
                                                                        id="hdfcFirstName"
                                                                        className="form-control input-underline p-0"
                                                                        placeholder=" "
                                                                        required
                                                                        onChange={(e) => {
                                                                            const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                                                            form.setFieldValue(field.name, value);
                                                                        }}
                                                                    />
                                                                )}
                                                            </Field>
                                                            <label className="label form-label" htmlFor='hdfcFirstName'>First Name</label>
                                                            <div className="underline"></div>
                                                            <ErrorMessage name="hdfcFirstName" component="div" className="text-danger small" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row floating-bottom">
                                                    <div className="col-12">
                                                        <div className="input-container has-icon">
                                                            <Field name="hdfcLastName">
                                                                {({ field, form }) => (
                                                                    <input
                                                                        {...field}
                                                                        type="text"
                                                                        id="hdfcLastName"
                                                                        className="form-control input-underline p-0"
                                                                        placeholder=" "
                                                                        required
                                                                        onChange={(e) => {
                                                                            const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                                                            form.setFieldValue(field.name, value);
                                                                        }}
                                                                    />
                                                                )}
                                                            </Field>
                                                            <label className="label form-label" htmlFor='hdfcLastName'>Last Name</label>
                                                            <div className="underline"></div>
                                                            <ErrorMessage name="hdfcLastName" component="div" className="text-danger small" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row floating-bottom">
                                                    <div className="col-12">
                                                        <label htmlFor="hdfcDob" className="form-label">Date of Birth</label>
                                                        <DatePicker
                                                            value={values?.dob}
                                                            onChange={(value) => setFieldValue('hdfcDob', value)}
                                                            label=""
                                                            required={true}
                                                            id="hdfcDob"
                                                            name="hdfcDob"
                                                            placeholder=""
                                                            autoComplete="off"
                                                        />
                                                        <ErrorMessage name="hdfcDob" component="div" className="text-danger small" />
                                                    </div>
                                                </div>
                                                <div className="row floating-bottom">
                                                    <div className="col-md-12">
                                                        <label htmlFor="hdfcRelation" className="form-label">Relationship</label>
                                                        <Field as="select" name="hdfcRelation" id="hdfcRelation" className="form-select input-underline kyc-summary-dropdown p-0 nominee-select">
                                                            <option value="">Select</option>
                                                            <option value="mother">Mother</option>
                                                            <option value="father">Father</option>
                                                            <option value="spouse">Spouse</option>
                                                            <option value="daughter">Daughter</option>
                                                            <option value="son">Son</option>
                                                        </Field>
                                                        <ErrorMessage name="hdfcRelation" component="div" className="text-danger small" />
                                                    </div>
                                                </div>

                                                <p class="Pname-i floating-bottom text-dark">Medical Reports</p>
                                                <div className="row">
                                                    <div className="col-12 floating-bottom">
                                                        <label>1. Have you ever suffered or are currently suffering from any of the following conditions ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport1" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport1" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>2. During the last 5 years have you undergone any major surgery or been hospitalized for more than one week ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport2" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport2" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>3. Do you take part in any adventurous sports or hobbies ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport3" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport3" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>4. Do you currently smoke or consume alcohol excessively ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport4" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport4" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>5. Has more than one of your parents or siblings died before the age of 60 due to health issues ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport5" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport5" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>6. Are you taking any medication for conditions not mentioned above ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport6" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport6" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>7. For Female Lives: Are you presently pregnant or have a history of complications during pregnancy ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport7" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport7" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>8. Have you ever been declined or had exclusions for any insurance cover ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport8" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport8" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>9. Have you ever been investigated or charged with any criminal/civil offences ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport9" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport9" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>10. Is your occupation associated with any hazards ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport10" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport10" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>11. Do you have any group risk cover through the same Master Policyholder ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport11" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport11" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-12 floating-bottom">
                                                        <label>12. Do you have any group risk cover through the same Master Policyholder ?</label>
                                                        <div className="d-flex gap-3 mt-2">
                                                            <label>
                                                                <Field type="radio" name="medicalReport12" value="yes" className='form-check-input' />
                                                                &nbsp;Yes
                                                            </label>
                                                            <label>
                                                                <Field type="radio" name="medicalReport12" value="no" className='form-check-input' />
                                                                &nbsp;No
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row addon-footer-top">
                                                    <div className="col-12">
                                                        <label className="form-check-label fs-12">
                                                            <Field type="checkbox" name="hdfcAgree" className="form-check-input" />
                                                            &nbsp;&nbsp;I hereby declare and confirm that the above details are true and correct.
                                                        </label>
                                                        <ErrorMessage name="hdfcAgree" component="div" className="text-danger small" />
                                                    </div>
                                                </div>

                                                <footer className="sticky-bottom-1 pe-24 cust-foot">

                                                    <div className="d-flex justify-content-end">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-primary"
                                                            disabled={isSubmitting}
                                                        >
                                                            Submit
                                                        </button>
                                                    </div>
                                                </footer>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>,
                document.body
            )}

            {showHdfcConfirm && ReactDOM.createPortal(
                <>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog bottom-modal">
                            <div className="modal-content custom-padding pt-0">
                                <div className="modal-header">
                                    <div className="top-draw"></div>
                                </div>

                                <div className="modal-body text-center p-0">
                                    <ImageComponent fileName='2fverify.svg' alt="2fverify" />
                                    <p className='fw-600 mt-4'>If you select 'Yes', your request cannot be processed. HDFC policy will not be issued. Confirm to choose YES</p>
                                </div>

                                <div className="modal-footer border-0 p-0">
                                    <div className="row w-100">
                                        <div className="col-6 ps-0">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary w-100"
                                                onClick={() => {
                                                    setShowHdfcConfirm(false);
                                                }}
                                            >
                                                No
                                            </button>
                                        </div>
                                        <div className="col-6 pe-0">
                                            <button
                                                type="button"
                                                className="btn btn-primary w-100"
                                                onClick={() => {
                                                    setShowHdfcConfirm(false);
                                                    setHdfcModal(false);
                                                    setCholaMsModal(false);
                                                }}
                                            >
                                                Yes
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>,
                document.body
            )}

            <footer className="sticky-bottom-1">
                <div className="container-fluid ">
                    <div className="row">
                        <div className="col-sm-12 ">
                            <div className="d-flex justify-content-end">

                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={submitDetail}
                                >
                                    Proceed
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
}

export default Addon;